import { del, get, patch, post, put } from '@/infra/rest';
import {
  CreateFrameworkResponse,
  FrameworkField,
  FrameworkPayload,
} from '../types';

// API Response interfaces
interface FrameworkStep {
  step: number;
  step_type: string;
  status: string;
  data: {
    type: string;
    url: string | null;
    column_titles?: Array<{
      index: number;
      key: string;
      ui_key: string;
    }>;
  };
  framework_id: string;
  id: string;
  created_at: string;
  updated_at: string;
  version: number;
}

// Framework Step Update interfaces
interface FrameworkStepUpdateRequest {
  framework_id: string;
  agent_type: string;
  id: string;
  step: number;
  step_type: string;
  status: string;
  data: {
    column_titles?: Array<{
      index: number;
      key: string;
      ui_key: string;
    }>;
    type: string;
    url?: string;
  };
}

interface FrameworkStepUpdateResponse {
  data: {
    framework: {
      framework_id: string;
      framework_name: string;
      agent_type: string;
      organization_id: string;
      created_by: string;
      status: string;
    };
    framework_steps: FrameworkStep[];
  };
}

// AI Testing Procedures API interfaces
interface AITestingProcedureRequest {
  agent_type: string;
  framework_name: string;
  framework_id: string;
  control_description: string;
  testing_procedures?: string; // Only for refine
}

// Utility function to handle backend capitalization changes
const normalizeBackendResponse = (
  columnTitles: Array<{ index: number; key: string; ui_key: string }>
) => {
  return columnTitles.map((item) => ({
    ...item,
    // Keep the backend's ui_key as returned, don't try to correct it
    ui_key: item.ui_key,
    key: item.key,
  }));
};

// Start Custom Framework Creation
export const createFrameworkAPI = async (
  payload: FrameworkPayload
): Promise<CreateFrameworkResponse> => {
  // Transform payload to match API contract with key/ui_key structure
  const columnTitles =
    payload.stepOneData.fields?.map((field, index) => ({
      index,
      key: field.key, // Use the backend key (fixed for mandatory fields)
      ui_key: field.ui_key, // Use ui_key for display
    })) || [];

  const requestPayload = {
    agent_type: 'gap_assessment',
    framework_name: payload.name,
    column_titles: columnTitles,
  };

  const response = (await post({
    url: '/v0/frameworks/create',
    data: requestPayload,
  })) as any;

  // Extract template URL and step IDs from framework_steps
  const templateStep = response.data.framework_steps.find(
    (step: any) => step.step_type === 'framework_controls_schema'
  );

  const controlsStep = response.data.framework_steps.find(
    (step: any) => step.step_type === 'framework_controls'
  );

  return {
    success: true,
    message: 'Framework creation started successfully',
    data: {
      id: response.data.framework.id, // Use 'id' instead of 'framework_id'
      name: response.data.framework.framework_name,
      templateUrl: templateStep?.data?.url || undefined,
      frameworkStatus: response.data.framework.status,
      schemaStepId: templateStep?.id,
      controlsStepId: controlsStep?.id,
      // Return the normalized column titles from backend response
      columnTitles: templateStep?.data?.column_titles
        ? normalizeBackendResponse(templateStep.data.column_titles)
        : undefined,
    },
    errors: null,
  };
};

// Update Framework Schema (Step 1 when framework already exists)
export const updateFrameworkSchemaAPI = async (
  frameworkStepId: string,
  frameworkId: string,
  fields: FrameworkField[]
): Promise<CreateFrameworkResponse> => {
  // Transform fields to the API format
  const columnTitlesArray = fields.map((field, index) => ({
    index,
    key: field.key, // Use the backend key
    ui_key: field.ui_key, // Use ui_key for display
  }));

  const requestPayload: FrameworkStepUpdateRequest = {
    framework_id: frameworkId,
    agent_type: 'gap_assessment',
    id: frameworkStepId,
    step: 0,
    step_type: 'framework_controls_schema',
    status: 'complete',
    data: {
      column_titles: columnTitlesArray,
      type: 'excel_template',
      url: '', // Will be populated by API
    },
  };

  const response = (await patch<FrameworkStepUpdateResponse>({
    url: `/v0/frameworks/step/${frameworkStepId}`,
    config: {
      data: requestPayload,
    },
  })) as any;

  // Extract updated template URL from framework_steps
  const templateStep = response.data.framework_steps.find(
    (step: any) => step.step_type === 'framework_controls_schema'
  );

  const controlsStep = response.data.framework_steps.find(
    (step: any) => step.step_type === 'framework_controls'
  );

  return {
    success: true,
    message: 'Framework schema updated successfully',
    data: {
      id: response.data.framework.framework_id,
      name: response.data.framework.framework_name,
      templateUrl: templateStep?.data?.url || undefined,
      frameworkStatus: response.data.framework.status,
      schemaStepId: templateStep?.id,
      controlsStepId: controlsStep?.id,
      // Return the normalized column titles from backend response
      columnTitles: templateStep?.data?.column_titles
        ? normalizeBackendResponse(templateStep.data.column_titles)
        : undefined,
    },
    errors: null,
  };
};

// Submit Filled Template
export const saveFrameworkDraftAPI = async (
  payload: FrameworkPayload
): Promise<CreateFrameworkResponse> => {
  const requestPayload = {
    agent_type: 'gap_assessment',
    framework_name: payload.name,
    framework_id: payload.stepTwoData.frameworkId || '',
    filled_template_url: payload.stepTwoData.filledTemplateUrl || '',
  };

  const response = (await post({
    url: '/v0/frameworks/template/submit',
    data: requestPayload,
  })) as any;

  // Extract controls URL from framework_steps where step_type is "framework_controls"
  const controlsStep = response.data.framework_steps.find(
    (step: any) => step.step_type === 'framework_controls'
  );

  return {
    success: true,
    message: 'Framework template submitted successfully',
    data: {
      id: response.data.framework.id,
      name: response.data.framework.framework_name,
      controlsUrl: controlsStep?.data?.url || undefined,
      frameworkStatus: response.data.framework.status,
    },
    errors: null,
  };
};

// Fetch controls data from URL
export const fetchControlsDataAPI = async (url: string): Promise<any> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch controls data: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching controls data:', error);
    throw error;
  }
};

// Update Framework
export const updateFrameworkAPI = async (
  id: string,
  payload: Partial<FrameworkPayload>
): Promise<CreateFrameworkResponse> => {
  const response = (await put({
    url: `/v0/frameworks/${id}`,
    data: payload,
  })) as any;

  return {
    success: true,
    message: 'Framework updated successfully',
    data: {
      id: id,
      name: payload.name || 'Updated Framework',
    },
    errors: null,
  };
};

// Delete Framework
export const deleteFrameworkAPI = async (id: string): Promise<void> => {
  await del({
    url: `/v0/frameworks/gap_assessment/${id}`,
  });
};

// Get Frameworks List (placeholder - you may need to add this endpoint)
export const getFrameworksListAPI = async (): Promise<any[]> => {
  try {
    const response = await get({
      url: '/v0/frameworks',
    });
    return (response as any).data || [];
  } catch (error) {
    console.warn('Frameworks list endpoint not available yet');
    return [];
  }
};

// Publish Framework
export const publishFrameworkAPI = async (
  payload: FrameworkPayload
): Promise<CreateFrameworkResponse> => {
  const requestPayload = {
    agent_type: 'gap_assessment',
    framework_name: payload.name,
    framework_id: payload.stepTwoData.frameworkId || '',
    // Make response_schema_url optional - backend will generate if not provided
    ...(payload.stepFourData.responseSchemaUrl && {
      response_schema_url: payload.stepFourData.responseSchemaUrl,
    }),
    // Add socket_id for publish tracking
    socket_id: payload.stepFourData.socketId || '',
  };

  const response = (await post({
    url: '/v0/frameworks/publish',
    data: requestPayload,
  })) as any;

  return {
    success: true,
    message: response.message || 'Framework publish request queued.',
    data: {
      id: response.data?.framework_id || payload.stepTwoData.frameworkId,
      name: payload.name,
      frameworkStatus: 'publishing', // Indicate publishing in progress
    },
    errors: null,
  };
};

// Create Testing Procedures With AI
export const createTestingProceduresWithAI = async (
  frameworkId: string,
  frameworkName: string,
  controlDescription: string
): Promise<string> => {
  const requestPayload: AITestingProcedureRequest = {
    agent_type: 'gap_assessment',
    framework_name: frameworkName,
    framework_id: frameworkId,
    control_description: controlDescription,
  };

  const response = (await post({
    url: '/v0/frameworks/controls/ai/testing-procedures/create',
    data: requestPayload,
  })) as any;

  return response.testing_procedures;
};

// Refine Testing Procedures With AI
export const refineTestingProceduresWithAI = async (
  frameworkId: string,
  frameworkName: string,
  controlDescription: string,
  testingProcedures: string
): Promise<{ control_description: string; testing_procedures: string }> => {
  const requestPayload = {
    agent_type: 'gap_assessment',
    framework_name: frameworkName,
    framework_id: frameworkId,
    control_description: controlDescription,
    testing_procedures: testingProcedures,
  };

  const response = (await post({
    url: '/v0/frameworks/controls/ai/testing-procedures/refine',
    data: requestPayload,
  })) as any;

  return {
    control_description: response.control_description,
    testing_procedures: response.testing_procedures,
  };
};

// Update Framework Controls (Step 3)
export const updateFrameworkControlsAPI = async (
  frameworkStepId: string,
  frameworkId: string,
  controlsUrl: string
): Promise<CreateFrameworkResponse> => {
  const requestPayload: FrameworkStepUpdateRequest = {
    framework_id: frameworkId,
    agent_type: 'gap_assessment',
    id: frameworkStepId,
    step: 1,
    step_type: 'framework_controls',
    status: 'input_needed',
    data: {
      type: 'controls_json',
      url: controlsUrl,
    },
  };

  const response = (await patch<FrameworkStepUpdateResponse>({
    url: `/v0/frameworks/step/${frameworkStepId}`,
    config: {
      data: requestPayload,
    },
  })) as any;

  return {
    success: true,
    message: 'Framework controls updated successfully',
    data: {
      id: response.data.framework.framework_id,
      name: response.data.framework.framework_name,
      frameworkStatus: response.data.framework.status,
    },
    errors: null,
  };
};

// Submit Framework Controls
export const submitFrameworkControlsAPI = async (
  frameworkId: string,
  frameworkName: string,
  controlsUrl: string // Back to accepting URL
): Promise<CreateFrameworkResponse> => {
  const requestPayload = {
    agent_type: 'gap_assessment',
    framework_name: frameworkName,
    framework_id: frameworkId,
    controls_url: controlsUrl, // Send the URL
  };

  const response = (await post({
    url: '/v0/frameworks/controls/submit',
    data: requestPayload,
  })) as any;

  // Extract response schema URL and step ID if present
  const responseSchemaStep = response.data.framework_steps.find(
    (step: any) => step.step_type === 'framework_response_schema'
  );

  return {
    success: true,
    message: 'Framework controls submitted successfully',
    data: {
      id: response.data.framework.framework_id,
      name: response.data.framework.framework_name,
      frameworkStatus: response.data.framework.status,
      responseSchemaUrl: responseSchemaStep?.data?.url || undefined,
    },
    errors: null,
  };
};

// Update Framework Response Schema (Step 4)

export const updateFrameworkResponseSchemaAPI = async (
  frameworkStepId: string,
  frameworkId: string,
  responseSchemaUrl: string
): Promise<CreateFrameworkResponse> => {
  const requestPayload: FrameworkStepUpdateRequest = {
    framework_id: frameworkId,
    agent_type: 'gap_assessment',
    id: frameworkStepId,
    step: 2,
    step_type: 'framework_response_schema',
    status: 'complete',
    data: {
      type: 'responses_json',
      url: responseSchemaUrl,
    },
  };

  const response = (await patch<FrameworkStepUpdateResponse>({
    url: `/v0/frameworks/step/${frameworkStepId}`,
    config: {
      data: requestPayload,
    },
  })) as any;

  return {
    success: true,
    message: 'Framework response schema updated successfully',
    data: {
      id: response.data.framework.framework_id,
      name: response.data.framework.framework_name,
      frameworkStatus: response.data.framework.status,
    },
    errors: null,
  };
};

// Add new API for creating control description with AI
export const createControlDescriptionWithAI = async (
  frameworkId: string,
  frameworkName: string,
  controlDescription: string
): Promise<string> => {
  const requestPayload = {
    agent_type: 'gap_assessment',
    framework_name: frameworkName,
    framework_id: frameworkId,
    control_description: controlDescription,
  };

  const response = (await post({
    url: '/v0/frameworks/controls/ai/control-description/refine',
    data: requestPayload,
  })) as any;

  return response.control_description;
};
